import {
  Accordion,
  Box,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
} from '@chakra-ui/react';
import { t } from 'i18next';
import { isEmpty } from 'lodash';
import { useCallback, useContext, useEffect, useMemo } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { Row, TableInstance } from 'react-table';
import { useAuth } from '~auth/useAuth';
import {
  FilterKeys,
  // Columns,
  // FilterOption,
  FilterOptionServerSide,
} from '~components/displays/DisplayTable/constants';
import {
  FilterContext,
  useFilterPersistenceManager,
  usePersistentFilters,
} from '~components/displays/DisplayTable/persistantFilters/persistantFilters';
import { DisplayPowerState, DisplayPresence } from '~components/displays/useDisplayPresence';
import { Status } from '~components/displays/useStatus';
import { SelectOption } from '~components/ui/Select';
import {
  DataWithCustomer,
  DisplayFilters,
} from '~pages/[organization]/customers/[handle]/displays/Index';
import { useAnalyticsReporter } from '~utils/analytics';
import { useFeatureFlag } from '~utils/features';
import { useSubscriptionType } from '~utils/subscriptions';
import { DisplayTable_DisplayFragment } from '../__generated__/DisplayTableServerSide.graphql';
import { DisplaysQueryContext } from '../useDisplaysQuery';
import { FilterButton } from './FilterButton';
import { FilterSectionServerSide } from './FilterSectionServerSide';
import { FilterTagListServerSide } from './FilterTagsServerSide';
import { useFilterOptionsServerSide } from './useFilterOptionsServerSide';

type Props = {
  table: TableInstance<DisplayTable_DisplayFragment>;
  data: DataWithCustomer;
  filtersState: DisplayFilters;
  handleSetFilters: (
    filterId: string,
    newValue: FilterOptionServerSide[],
    resetPagination?: boolean,
  ) => void;
  /** Callback when initial persisted filters have been applied */
  onFiltersReady?: (isReady: boolean) => void;
};

// type PropsWarning = {
//     table: TableInstance<DisplayTable_DisplayFragment>;
// };

export function FilterServerSide({ data, filtersState, handleSetFilters, onFiltersReady }: Props) {
  const { user } = useAuth();
  const { setDisplaysQuery } = useContext(DisplaysQueryContext);

  const filters = useFilterOptionsServerSide(filtersState, data);
  const allEnabledFilters = useMemo(
    () => [
      ...filters.groups.enabled,
      ...filters.site.enabled,
      ...filters.firmware.enabled,
      ...filters.playlist.enabled,
      ...filters.powerSchedule.enabled,
      ...filters.warnings.enabled,
      ...filters.status.enabled,
      ...filters.configuration.enabled,
    ],
    [filters],
  );

  const allAvailableFilters = useMemo(
    () => [
      ...filters.groups.all,
      ...filters.site.all,
      ...filters.firmware.all,
      ...filters.playlist.all,
      ...filters.powerSchedule.all,
      ...filters.warnings.all,
      ...filters.status.all,
      ...filters.configuration.all,
    ],
    [filters],
  );

  const analytics = useAnalyticsReporter();
  const { hasWaveSubscription } = useSubscriptionType(data.customer);
  const { isEnabled: isSchedulesEnabled } = useFeatureFlag('schedules');

  // Determine context based on current route
  // Get customer handle from URL - available immediately without GraphQL
  const { configurationId, customer: customerHandle } = useParams();
  const { pathname } = useLocation();
  const isInConfigurationSummary = Boolean(configurationId) && pathname.endsWith('/config-devices');
  const { displaysQuery } = useContext(DisplaysQueryContext);
  const claimCodeParam = displaysQuery.get('claimCode');
  const claimCodeId = claimCodeParam ? claimCodeParam.split('|')[2] : undefined;
  const filterContext = claimCodeParam
    ? FilterContext.CLAIM_CODES_DISPLAY
    : isInConfigurationSummary
    ? FilterContext.CONFIGURATION
    : FilterContext.DISPLAYS;

  const { resetCustomerFilters } = usePersistentFilters(
    user?.subId ?? '',
    customerHandle ?? '',
    filterContext,
    undefined,
    claimCodeId,
  );

  const filtersReady =
    filters.groups.enabled.length +
      filters.site.enabled.length +
      filters.firmware.enabled.length +
      filters.playlist.enabled.length +
      filters.powerSchedule.enabled.length +
      filters.warnings.enabled.length +
      filters.status.enabled.length +
      filters.configuration.enabled.length >
    0;

  // Build backendMap for filter validation/repair
  const backendMap = useMemo<Record<FilterKeys, Set<string>>>(() => {
    return {
      groups: new Set(data.customer?.groups?.map((g) => g.id) ?? []),
      site: new Set(data.customer?.sites?.map((s) => s.id) ?? []),
      firmware: new Set(data.customer?.displayFirmwareVersions ?? []),
      playlist: new Set(data.customer?.playlists?.map((p) => p.id) ?? []),
      powerSchedule: new Set(
        (isSchedulesEnabled ? data.customer?.schedules : data.customer?.powerSchedules)?.map(
          (ps) => ps.id,
        ) ?? [],
      ),
      warnings: new Set(data.customer?.warningList.map((o) => o.key)),
      status: new Set(filters.status.all.map((o) => o.id)),
      claimCode: new Set(),
      configuration: new Set(data.customer?.configurations?.map((c) => c.id) ?? []),
    };
  }, [
    data.customer?.configurations,
    data.customer?.displayFirmwareVersions,
    data.customer?.groups,
    data.customer?.playlists,
    data.customer?.powerSchedules,
    data.customer?.schedules,
    data.customer?.sites,
    isSchedulesEnabled,
    filters.status.all,
    data.customer?.warningList,
  ]);

  const { createFilterHandler, isReady: filtersInitialized } =
    useFilterPersistenceManager<FilterOptionServerSide>({
      userId: user?.subId ?? '',
      customerHandle: customerHandle ?? '',
      customerId: data?.customer?.id,
      claimCodeId,
      context: filterContext,
      allEnabledFilters: allAvailableFilters,
      backendMap,
      onApplyFilters: handleSetFilters,
      filterStateExists: (columnId: string) => filtersState.some((f) => f.id === columnId),
      filtersReady,
    });

  // Notify parent when initial filters have been applied
  useEffect(() => {
    onFiltersReady?.(filtersInitialized);
  }, [filtersInitialized, onFiltersReady]);

  const persistFilter = createFilterHandler<FilterOptionServerSide>();

  // Helper to clear URL params when filters are removed - syncs with DisplaysQueryContext
  // This prevents the URL "blink" effect caused by useDisplaysQuery re-syncing from context
  const clearUrlParam = useCallback(
    (filterId: string) => {
      setDisplaysQuery?.((prevParams) => {
        const newParams = new URLSearchParams(prevParams);
        newParams.delete(filterId);
        return newParams;
      });
    },
    [setDisplaysQuery],
  );

  const toggleFilter = useCallback(
    (filter: FilterOptionServerSide) => {
      const currentFilter = filtersState.find((f) => f.id === filter.column);
      const isEnabled = currentFilter?.value.some((v) => v.id === filter.id);

      const newValue = isEnabled
        ? currentFilter?.value.filter((v) => v.id !== filter.id) ?? []
        : [...(currentFilter?.value ?? []), filter];

      if (!isEnabled) {
        analytics.track('displayFilter', { filter: filter.column });
      } else {
        // Only clear URL param when removing a filter (not when adding)
        if (newValue.length === 0) {
          clearUrlParam(filter.column);
        }
      }

      persistFilter(filter.column, newValue);
    },
    [filtersState, persistFilter, analytics, clearUrlParam],
  );

  const disableFilter = useCallback(
    (filter: FilterOptionServerSide) => {
      const currentFilter = filtersState.find((f) => f.id === filter.column);
      const newValue = currentFilter?.value.filter((v) => v.id !== filter.id) ?? [];

      persistFilter(filter.column, newValue);
      // Clear URL param when removing filter (only if all values for this filter are gone)
      if (newValue.length === 0) {
        clearUrlParam(filter.column);
      }
    },
    [filtersState, persistFilter, clearUrlParam],
  );

  const resetAllFilters = useCallback(() => {
    filtersState.forEach((filter) => {
      handleSetFilters(filter.id, []);
      // Clear URL params for each filter
      clearUrlParam(filter.id);
    });
    resetCustomerFilters();
  }, [filtersState, handleSetFilters, resetCustomerFilters, clearUrlParam]);

  return (
    <Box display="flex" alignItems="center">
      <Popover closeOnBlur closeOnEsc placement="bottom-start" isLazy>
        <PopoverTrigger>
          <FilterButton enabledFilterCount={allEnabledFilters.length} />
        </PopoverTrigger>

        <PopoverContent color="gray.900" width="430px">
          <PopoverBody>
            <Accordion allowToggle>
              <FilterSectionServerSide
                name={t('groups')}
                filters={filters.groups}
                onToggle={toggleFilter}
              />
              <FilterSectionServerSide
                name={t('sites')}
                filters={filters.site}
                onToggle={toggleFilter}
              />
              <FilterSectionServerSide
                name={t('firmware')}
                filters={filters.firmware}
                onToggle={toggleFilter}
              />
              <FilterSectionServerSide
                name={t('playlists')}
                filters={filters.playlist}
                onToggle={toggleFilter}
              />
              <FilterSectionServerSide
                name={t('schedules')}
                filters={filters.powerSchedule}
                onToggle={toggleFilter}
              />
              <FilterSectionServerSide
                name={t('warnings')}
                filters={filters.warnings}
                onToggle={toggleFilter}
              />
              <FilterSectionServerSide
                name={t('status')}
                filters={filters.status}
                onToggle={toggleFilter}
              />
              {hasWaveSubscription && !isInConfigurationSummary ? (
                <FilterSectionServerSide
                  name={t('configurations')}
                  filters={filters.configuration}
                  onToggle={toggleFilter}
                />
              ) : undefined}
            </Accordion>
          </PopoverBody>
        </PopoverContent>
      </Popover>

      <Box marginLeft="4">
        <FilterTagListServerSide
          filters={allEnabledFilters}
          onDisable={disableFilter}
          onReset={resetAllFilters}
        />
      </Box>
    </Box>
  );
}

export function filterBySiteServerSide(
  rows: Array<Row<DisplayTable_DisplayFragment>>,
  columnIds: string[],
  filterValues: SelectOption[],
) {
  return filterValues.length
    ? rows.filter((row) => {
        return filterValues.some((filter) => {
          return filter.value === row.original.site?.id;
        });
      })
    : rows;
}

export function filterByConfigurationServerSide(
  rows: Array<Row<DisplayTable_DisplayFragment>>,
  columnIds: string[],
  filterValues: SelectOption[],
) {
  return filterValues.length
    ? rows.filter((row) => {
        return filterValues.some((filter) => {
          return filter.value === row.original.configuration?.name;
        });
      })
    : rows;
}

export function filterByFirmwareServerSide(
  rows: Array<Row<DisplayTable_DisplayFragment>>,
  columnIds: string[],
  filterValues: SelectOption[],
) {
  return isEmpty(filterValues)
    ? rows
    : rows.filter((row) => {
        return filterValues.some((filter) => {
          const firmware = row.original.firmware?.android.version;
          return filter.value === firmware;
        });
      });
}

export function filterByPlaylistServerSide(
  rows: Array<Row<DisplayTable_DisplayFragment>>,
  columnIds: string[],
  filterValues: SelectOption[],
) {
  return isEmpty(filterValues)
    ? rows
    : rows.filter((row) => {
        return filterValues.some((filter) => {
          return filter.value === row.original.playlist?.current?.title;
        });
      });
}

export function filterByPowerScheduleServerSide(
  rows: Array<Row<DisplayTable_DisplayFragment>>,
  columnIds: string[],
  filterValues: SelectOption[],
) {
  return isEmpty(filterValues)
    ? rows
    : rows.filter((row) => {
        return filterValues.some((filter) => {
          return (
            filter.value === row.original.powerSchedule?.schedule?.title ||
            filter.value === row.original.schedule?.schedule?.title
          );
        });
      });
}

/**
 * Handles a single row by status (as opposed to multiple ones like the other filter methods here).
 * The `status` that we need cannot be obtained here since it relies on hooks, so parsing the array of rows has been
 * lifted to the `DisplayTable` component instead.
 */
export function filterRowByWarningStatusServerSide(
  row: Row<DisplayTable_DisplayFragment>,
  status: Status,
  filterValues: SelectOption[],
) {
  if (isEmpty(filterValues)) {
    return row;
  }

  const shouldRenderRow = filterValues.some((filter) => {
    if (status.kind === 'ok' && filter.value === 'No warnings') {
      return true;
    } else if (status.kind === 'warnings') {
      return status.warnings.find(({ message }) => message === filter.value) !== undefined;
    } else if (status.kind === 'error') {
      return status.errorMessage === filter.value;
    }
  });

  return shouldRenderRow ? row : null;
}

export function filterRowByStatusServerSide(
  row: Row<DisplayTable_DisplayFragment>,
  presence: DisplayPresence,
  powerState: DisplayPowerState,
  filterValues: SelectOption[],
) {
  if (isEmpty(filterValues)) {
    return row;
  }

  const presenceFilters: SelectOption[] = [];
  const powerStateFilters: SelectOption[] = [];

  filterValues.forEach((filter) => {
    if (['Connected', 'Disconnected'].includes(filter.label)) {
      presenceFilters.push(filter);
    }
    if (['Powered on', 'Standby'].includes(filter.label)) {
      powerStateFilters.push(filter);
    }
  });

  const rowMatchesPresenceFilters =
    presenceFilters.length === 0 ||
    presenceFilters.some((filter) => presence.label === filter.value);
  const rowMatchesPowerStateFilters =
    powerStateFilters.length === 0 ||
    powerStateFilters.some((filter) => powerState.label === filter.value);

  const rowMatchesFilters = rowMatchesPresenceFilters && rowMatchesPowerStateFilters;

  return rowMatchesFilters ? row : null;
}

export function filterByGroupsServerSide(
  rows: Array<Row<DisplayTable_DisplayFragment>>,
  columnIds: string[],
  filterValues: SelectOption[],
) {
  return filterValues.length
    ? rows.filter((row) => {
        return filterValues
          .map((filter) => filter.value)
          .some((id) => {
            return (row.original.groups?.map((group) => group.id) ?? []).indexOf(id) > -1;
          });
      })
    : rows;
}
